import { array, object, string } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { uniq } from 'lodash-es'

import { formatDoubleBookingRules } from 'components/availability/form/utils'
import { getFormValue } from 'components/event_type_edit'
import { getPoolingTypeKinds } from 'services/event_types'

import {
  filterByPoolingType,
  filterByPoolUid,
  getInvitationIds,
  getUserIds,
  reassignUserPoolsPositions,
  withoutDeleted
} from './helpers'
import { sortHosts } from '../../utils'
import { filterEnabledMemberships } from '../helpers'

export const MAX_ROUND_ROBIN_USER_POOLS = 5
const MAX_COLLECTIVE_USER_POOLS = 1
const MAX_USER_POOL_NAME_LENGTH = 50

const defaultAllocationPreference = 'availability'

const getUserPoolsInitialValues = ({ eventType }) => {
  const { isCollectiveRoundRobin, user_pools: userPools = [] } = eventType
  if (!isCollectiveRoundRobin) { return {} }

  return {
    userPools: reassignUserPoolsPositions(userPools)
  }
}

export const getInitialValues = ({ eventType, isCreation }) => {
  const {
    isCollectiveRoundRobin,
    isMultiHost,
    isMETParent,
    isTeam,
    allocation_preference: initialAllocationPreference,
    lock_reschedule_members: lockRescheduleMembers,
    memberships: initialMemberships,
    pooling_type: poolingType
  } = eventType

  if (isMETParent) {
    return { pooling_type: poolingType }
  }

  const sortedMemberships = sortHosts(initialMemberships.slice())

  const memberships = sortedMemberships.map(
    ({ location_configuration: locationConfiguration, ...membership }) => ({
      locationConfiguration,
      ...membership,
      ...(isCollectiveRoundRobin ? { user_pool_uid: membership.user_pool_uuid } : {}),
      ...((isCreation && isTeam && isMultiHost) ? { enabled: true } : {}),
    })
  )

  const collectiveOrRoundRobinInitialValues = isMultiHost ? {
    allocationPreference: isCreation ? defaultAllocationPreference : initialAllocationPreference,
    lockRescheduleMembers: lockRescheduleMembers.toString(),
  } : {}

  return {
    pooling_type: poolingType,
    memberships,
    ...collectiveOrRoundRobinInitialValues,
    ...getUserPoolsInitialValues({ eventType })
  }
}

const getDoubleBookingRules = (doubleBookingRuleMemberships, id) => (
  doubleBookingRuleMemberships ? doubleBookingRuleMemberships.find(({ id: mId }) => mId === id)?.doubleBookingRules : {}
)

export const formatHostListMemberships = (values) => ({
  event_type_memberships_attributes: values.memberships.map(
    ({ locationConfiguration: _, double_booking_rules: __, ...membership }) => ({
      ...membership,
      availability_rule_reference_attributes: {
        double_booking_rules_attributes: formatDoubleBookingRules(
          getDoubleBookingRules(values.doubleBookingRuleMemberships, membership.id)
        )
      }
    })
  )
})

export const getFormatValuesToServerData = ({ eventType, isCreation }) => (values) => {
  const { isCollectiveRoundRobin, isMultiHost } = getPoolingTypeKinds(values.pooling_type)

  if (eventType.isMETParent) { return {} }

  const membershipsAttributes = formatHostListMemberships(values)

  if (!isMultiHost && !eventType.isTeam) return membershipsAttributes

  const memberships = filterEnabledMemberships(values.memberships)
  const organizationMembershipIds = getUserIds(memberships)
  const organizationInvitationIds = getInvitationIds(memberships)

  const attributes = {
    ...membershipsAttributes,
    selected_member_ids: organizationMembershipIds,
    selected_invitation_ids: organizationInvitationIds,
    lock_reschedule_members: values.lockRescheduleMembers === 'true',
    allocation_preference: values.allocationPreference,
    pooling_type: values.pooling_type,
  }

  if (!isCollectiveRoundRobin) return attributes

  let userPools = values.userPools

  if (isCreation) {
    const notEmptyUserPoolUids = uniq(values.memberships.map((membership) => membership.user_pool_uid))
    userPools = values.userPools.filter(({ uuid, key }) => notEmptyUserPoolUids.includes(uuid || key))
  }

  return {
    ...attributes,
    user_pools_attributes: userPools.map(({ key, _destroy, ...userPool }) => {
      const userPoolUid = userPool.uuid || key
      const userPoolMemberships = filterByPoolUid(memberships, userPoolUid)
      const userPoolMembershipIds = getUserIds(userPoolMemberships)
      const userPoolInvitationIds = getInvitationIds(userPoolMemberships)

      const markForDestruction = _destroy || userPoolMemberships.length === 0
      if (markForDestruction && !userPool.uuid) return null

      return {
        // we need to have the `id` column to make it work
        // currently in Rails uses `id` column to find existing data,
        // and we don't have it in this table, so this is workaround
        // Related issue: https://github.com/rails/rails/issues/48714
        id: userPool.uuid,
        ...userPool,
        selected_member_ids: userPoolMembershipIds,
        selected_invitation_ids: userPoolInvitationIds,
        _destroy: markForDestruction
      }
    }).filter(Boolean)
  }
}

const getUserPoolsValidationSchema = () => object({
  userPools: array().when('pooling_type', {
    is: (type) => type === 'multi_pool',
    then: array().of(object({
      name: string().when('pooling_type', {
        is: 'round_robin',
        then: string().trim().max(MAX_USER_POOL_NAME_LENGTH).required(
          I18n.t('publisher.event_type_editor.sections.hosts.round_robin.pool_name_validation'
          )),
        otherwise: string().nullable()
      })
    })).test('doesNotExceedMaxPoolsCount', '', (value, { createError }) => {
      if (!value) { return true }

      const activeUserPools = withoutDeleted(value)
      const collectivePoolsCount = filterByPoolingType(activeUserPools, 'collective').length
      const roundRobinPoolsCount = filterByPoolingType(activeUserPools, 'round_robin').length

      if (collectivePoolsCount > MAX_COLLECTIVE_USER_POOLS) {
        return createError({
          message: I18n.t('publisher.event_types.members.validation.too_many_standing_pools')
        })
      }

      if (roundRobinPoolsCount > MAX_ROUND_ROBIN_USER_POOLS) {
        return createError({
          message: I18n.t('publisher.event_types.members.validation.max_pools_reached')
        })
      }

      return true
    })
  })
})

const getMembershipsValidationSchema = ({ isTeam }) => object({
  memberships: array().test('atLeastOneHost', '', (value, { createError }) => {
    if (filterEnabledMemberships(value).length === 0) {
      const message = isTeam
        ? I18n.t('publisher.event_type_editor.sections.hosts_and_invitees.errors.no_hosts')
        : I18n.t('publisher.event_type_editor.sections.hosts.errors.no_hosts')

      return createError({ message })
    }

    return true
  })
})

export const getValidationSchema = ({ eventType }) => {
  const { isMultiHost, isTeam } = eventType
  const isEventTypeWithMemberships = isMultiHost || isTeam
  const mayHaveUserPools = isMultiHost

  return object({})
    .concat(isEventTypeWithMemberships ? getMembershipsValidationSchema({ isTeam }) : object({}))
    .concat(mayHaveUserPools ? getUserPoolsValidationSchema() : object({}))
}

export const prepareValuesForPreview = (eventType, formValues) => {
  const { pooling_type: poolingType, memberships } = eventType
  const { isMultiHost, isCollectiveRoundRobin } = getPoolingTypeKinds(
    formValues.pooling_type || poolingType
  )
  const { memberships: formMemberships } = formValues

  const previewMemberships = formMemberships
    ? formMemberships.map(
      ({ locationConfiguration, ...membership }) => ({
        location_configuration: locationConfiguration,
        ...membership,
      })
    )
    : memberships

  return {
    pooling_type: getFormValue(eventType.pooling_type, formValues.pooling_type),
    memberships: previewMemberships,
    ...(isMultiHost ? {
      allocation_preference: getFormValue(eventType.allocation_preference, formValues.allocationPreference),
      lock_reschedule_members: getFormValue(eventType.lock_reschedule_members, formValues.lockRescheduleMembers),
    } : {}),
    ...(isCollectiveRoundRobin ? ({ user_pools: getFormValue(eventType.user_pools, formValues.userPools) }) : {})
  }
}
